import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import usePublishBlast from '../../../api/usePublishBlast';
import { extractBlastErrorCode, extractBlastErrorMessage } from '../blastFormUtils';
import type { BlastDetail } from '../../../types';
type Props = {
    blast: BlastDetail;
    audienceSize: number | null;
    audienceSizeLoading: boolean;
    audienceSizeError: boolean;
    onRetryAudienceSize: () => void;
    onClose: () => void;
    onSuccess: () => void;
    onSchedule: () => void;
};
const SYNC_PUBLISH_CAP = 50000;
const PublishModal: React.FC<Props> = ({
    blast,
    audienceSize,
    audienceSizeLoading,
    audienceSizeError,
    onRetryAudienceSize,
    onClose,
    onSuccess,
    onSchedule
}) => {
    const publish = usePublishBlast();
    const headingId = useId();
    const capExceeded =
        (audienceSize != null && audienceSize > SYNC_PUBLISH_CAP) ||
        extractBlastErrorCode(publish.error) === 'segment_too_large';
    const handlePublish = () => {
        publish.mutate(
            { blast_id: blast.blast_id },
            {
                onSuccess: () => onSuccess(),
                onError: () => {}
            }
        );
    };
    return (
        <MarkModal
            onClose={onClose}
            isPending={publish.isPending}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    Publish blast
                </MarkHeading>
                {capExceeded ? (
                    <>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            Segment has{' '}
                            {audienceSize != null ? audienceSize.toLocaleString() : 'too many'}{' '}
                            recipients — over the {SYNC_PUBLISH_CAP.toLocaleString()} sync-publish
                            cap. Use Schedule instead.
                        </P>
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Scheduled blasts snapshot at dispatch.
                        </P>
                    </>
                ) : (
                    <>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            This will send <strong>{blast.name}</strong> immediately to all matched
                            recipients.
                        </P>
                        {blast.message_class === 'TRANSACTIONAL' && (
                            <div
                                style={{
                                    marginBottom: '0.8rem'
                                }}
                            >
                                <Tag type="warn" size="small">
                                    TRANSACTIONAL
                                </Tag>
                                <P
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-warning)',
                                        marginTop: '0.4rem'
                                    }}
                                >
                                    You will be recorded as approver. Bypasses unsubscribe
                                    suppression.
                                </P>
                            </div>
                        )}
                    </>
                )}
                {audienceSizeError && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            Could not load audience size.{' '}
                            <MarkButton type="button" onClick={onRetryAudienceSize}>
                                Retry
                            </MarkButton>
                        </P>
                    </div>
                )}
                {publish.error && !capExceeded && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {extractBlastErrorMessage(publish.error)}
                        </P>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.8rem',
                        marginTop: '1.6rem'
                    }}
                >
                    <MarkButton type="button" onClick={onClose} disabled={publish.isPending}>
                        Cancel
                    </MarkButton>
                    {capExceeded ? (
                        <MarkButton type="button" onClick={onSchedule} variant="primary">
                            Schedule instead
                        </MarkButton>
                    ) : (
                        <MarkButton
                            variant="primary"
                            type="button"
                            onClick={handlePublish}
                            disabled={publish.isPending || audienceSizeLoading || audienceSizeError}
                        >
                            {audienceSizeLoading
                                ? 'Loading audience…'
                                : audienceSizeError
                                  ? 'Audience unavailable'
                                  : publish.isPending
                                    ? 'Publishing…'
                                    : 'Publish now'}
                        </MarkButton>
                    )}
                </div>
            </Section>
        </MarkModal>
    );
};
export default PublishModal;
